import React, { useEffect, useState } from 'react';
import { Box, Breadcrumbs, Button, Link, Skeleton, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormControlledTextField } from '../../common/Form/FormTextField';
import { FormControlledNumberField } from '../../common/Form/FormNumberField';
import ResourceSelector from '../../common/ResourceSelector';
import { FieldRequiredMark } from '../../common/Form/FieldRequiredMark';
import Loader from '../../common/Loader';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import { getData, postData } from '../../../DataAccessLayer';
import { summaries, modules as modulesService } from '../../../DataAccessLayer/services';

function SummaryGenerator() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const courseId = useSelector((state) => state.course.courseId);

  const [modulesList, setModulesList] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isModulesError, setIsModulesError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: { title: '', resources: [], targetWordCount: 500 },
  });

  const loadModules = async () => {
    setIsLoadingModules(true);
    setIsModulesError(false);
    try {
      const data = await getData(modulesService(courseId));
      setModulesList(data);
    } catch (err) {
      console.error(err);
      setIsModulesError(true);
    } finally {
      setIsLoadingModules(false);
    }
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const onSubmit = async ({ title, resources, targetWordCount }) => {
    setIsSubmitting(true);
    try {
      const selectedResourceNames = modulesList
        .filter((module) => resources.includes(module.id))
        .map((module) => module.name);

      const newSummary = await postData(summaries(), {
        title: title.trim(),
        resources: selectedResourceNames,
        targetWordCount: Number(targetWordCount),
        courseId,
      });
      navigate(`${pathname}/${newSummary.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate summary notes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="mss__centeredScreen mss__generator" id="summaryGenerator">
      <Loader id="summaryGenerator__submit" isOpen={isSubmitting} />
      <Breadcrumbs className="mss__breadcrumbs" aria-label="breadcrumb">
        <Link component={RouterLink} to="..">
          myStudySidekick
        </Link>
        <Typography>Summary Notes Generator</Typography>
      </Breadcrumbs>

      {isModulesError && (
        <MssAlert id="generator__alert" severity="error" type="statusAlert">
          <MssApiError
            id="generator"
            widgetName="Summary Notes Generator"
            isPage
            reload={loadModules}
          />
        </MssAlert>
      )}

      {!isModulesError && (
        <>
          <Box className="generatorHeading">
            <Typography variant="h2" component="h1">
              Generate Summary Notes
            </Typography>
          </Box>

          <Box className="mss__box generatorForm" id="summaryGenerator__box_form">
            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">
                Summary Notes Title
              </Typography>

              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <FormControlledTextField
                  name="title"
                  control={control}
                  ariaLabel="Summary Notes Title"
                  placeholder="Enter Summary Notes Title"
                  fullWidth
                />
              )}
            </Box>

            <Box className="formSection">
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={160} />
              ) : (
                <Controller
                  name="resources"
                  control={control}
                  rules={{
                    validate: (value) => value.length > 0 || 'Select at least one resource',
                  }}
                  render={({ field }) => (
                    <ResourceSelector
                      id="summaryGenerator"
                      modules={modulesList}
                      selected={field.value ?? []}
                      onChange={field.onChange}
                      helperText="Select resources from which the summary will be generated."
                      required
                    />
                  )}
                />
              )}
            </Box>

            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">
                Summary Settings <FieldRequiredMark label="Target word count" />
              </Typography>
              <Typography className="sectionHelper">Target Word Count</Typography>
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <FormControlledNumberField
                  name="targetWordCount"
                  control={control}
                  rules={{
                    required: 'Enter a target word count',
                    min: { value: 1, message: 'Target word count must be greater than 0' },
                  }}
                  ariaLabel="Target word count"
                  placeholder="Enter a target word count"
                  htmlInputProps={{ min: 1 }}
                  className="targetWordCountField"
                />
              )}
              <Box className="mss__formActions">
                <Button
                  variant="contained"
                  className="mss__button primary"
                  disabled={!isValid || isSubmitting || isLoadingModules}
                  onClick={handleSubmit(onSubmit)}
                >
                  Generate Summary Note
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default SummaryGenerator;
